import { useState } from 'react';

import { Check, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';

import { useTimeOffReviewActions } from '../../../hooks/use-time-off-review-actions';
import { TimeOffRequest } from '../../../types/time-off-request';
import { TimeOffReviewDecision } from '../../../types/time-off-review';
import { ReviewTimeOffModal } from '../modals/review-time-off-modal';

interface TimeOffApprovalActionsCellProps {
  request: TimeOffRequest;
}

/**
 * Approve (green ✓) / decline (red ✕) actions for a request row.
 *
 * Actions are only available while the overall request is still PENDING and
 * UPCOMING — once it reaches a final status (APPROVED / DECLINED / CANCELLED) or
 * the dates have passed, no action is shown (the backend rejects late changes).
 *
 * Within a PENDING request the buttons reflect THIS approver's own decision (a
 * request can have multiple approvers):
 *   - approver still pending → both approve + decline
 *   - approver already approved → decline only (reverse own decision)
 *   - approver already rejected → approve only (reverse own decision)
 *
 * Requires the TimeOffsApprove policy.
 */
export const TimeOffApprovalActionsCell = ({ request }: TimeOffApprovalActionsCellProps) => {
  const { t } = useTranslation('authenticated.timeOff');
  const { canShowApprove, canShowDecline, hasAnyAction } = useTimeOffReviewActions(request);
  const [decision, setDecision] = useState<TimeOffReviewDecision | null>(null);

  if (!hasAnyAction) {
    return null;
  }

  return (
    // Stop propagation so the action buttons never trigger any row-level handler.
    <div onClick={event => event.stopPropagation()} className="flex items-center gap-1">
      {canShowDecline && (
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 text-destructive hover:bg-destructive/10 hover:text-destructive"
          aria-label={t('approvals.actions.decline')}
          onClick={() => setDecision(TimeOffReviewDecision.Decline)}
        >
          <X className="h-4 w-4" />
        </Button>
      )}
      {canShowApprove && (
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 text-success hover:bg-success/10 hover:text-success"
          aria-label={t('approvals.actions.approve')}
          onClick={() => setDecision(TimeOffReviewDecision.Approve)}
        >
          <Check className="h-4 w-4" />
        </Button>
      )}

      {decision && (
        <ReviewTimeOffModal
          request={request}
          decision={decision}
          open
          onClose={() => setDecision(null)}
        />
      )}
    </div>
  );
};
